import { createActorContext } from '@xstate/react';
import { match } from 'ts-pattern';
import { v4 as uuidv4 } from 'uuid';
import { assign, fromPromise, raise, setup } from 'xstate';

import { STORY_COPY } from 'copy/story';

import { useMomentStory } from 'gql/api/queries/moment-story.generated';
import { getMomentInventory } from 'queries/api/moment-nft-items';
import { isNumberType } from 'utils/helpers';
import {
  MomentInventoryItem,
  generateBlockquoteJsonFromText,
  generateH1JsonFromText,
  generateH2JsonFromText,
  generateParagraphJsonFromText,
  isTextStoryGroup,
  mapApiStoryToReducerState,
} from 'utils/story/story';
import {
  STORY_PARAGRAPH_PLACEHOLDER,
  getInitialStory,
} from 'utils/story/story.fixtures';

import {
  StoryTextBlock,
  StoryBlock,
  StoryTextBlockType,
  StoryGroup,
} from 'types/Story';

const STORY_GROUP_LIMIT = 100;

export type EditorContext = {
  momentId: number | null;
  groups: StoryGroup[];
  focusedGroupId: string | null;
  lastFocusedGroupId: string | null;
  caretPosition: number;
  contentLength: number;
  momentItems: MomentInventoryItem[];
};

export type EditorEvent =
  // editor events
  | { type: 'FOCUS_GROUP'; groupId: string }
  | { type: 'BLUR_GROUP'; groupId: string }
  | {
      type: 'UPDATE_EDITOR_STATE';
      groupId: string;
      caretPosition: number;
      contentLength: number;
    }
  | { type: 'FETCH_SUCCESS'; data: StoryGroup[] }
  | { type: 'SET_MOMENT_ID'; momentId: number }
  // group events
  | { type: 'CREATE_GROUP'; groupId: string; items: StoryBlock[] }
  | { type: 'REMOVE_GROUP'; groupId: string }
  | { type: 'UPDATE_GROUP_SIZE'; groupId: string; size: StoryGroup['size'] }
  | { type: 'SET_GROUPS'; groups: StoryGroup[] }
  // block events
  | { type: 'UPDATE_BLOCK'; block: StoryBlock }
  | { type: 'REMOVE_BLOCK'; blockId: string }
  | { type: 'ADD_BLOCK_TO_GROUP'; block: StoryBlock; groupId: string }
  | {
      type: 'UPDATE_BLOCK_TYPE';
      contentType: StoryTextBlockType;
      contentText: string;
      blockId: string;
    }
  // keyboard events
  | { type: 'PRESS_BACKSPACE'; groupId: string }
  | { type: 'PRESS_ENTER' }
  | { type: 'PRESS_ARROW_DOWN' }
  | { type: 'PRESS_ARROW_UP' }
  | { type: 'SCROLL_TO_GROUP' }
  | { type: 'SCROLL_COMPLETE' };

const storyFetcher = fromPromise<
  {
    groups: StoryGroup[];
    momentItems: MomentInventoryItem[];
  },
  { momentId: number }
>(async (options) => {
  const getMomentStory = useMomentStory.fetcher({
    momentId: options.input.momentId,
  });

  const [momentStoryQuery, momentItems] = await Promise.all([
    getMomentStory(),
    getMomentInventory({ momentId: options.input.momentId }),
  ]);

  if (!momentStoryQuery.momentStory) {
    return {
      groups: getInitialStory(),
      momentItems,
    };
  }

  const mappedStory = mapApiStoryToReducerState(momentStoryQuery.momentStory);

  if (mappedStory.length === 0) {
    return {
      groups: getInitialStory(),
      momentItems,
    };
  } else {
    return {
      groups: mappedStory,
      momentItems,
    };
  }
});

export const editorMachine = setup({
  types: {} as {
    context: EditorContext;
    events: EditorEvent;
    input: EditorContext;
    tags: 'loading' | 'ready' | 'scrolling';
  },
  delays: {
    timeout: 250,
  },
  actors: {
    storyFetcher,
  },
  actions: {
    /**
     * block level actions
     */
    removeBlock: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'REMOVE_BLOCK') {
          return context.groups;
        }
        return context.groups.map((group) => ({
          ...group,
          items: group.items.filter((block) => block.id !== event.blockId),
        }));
      },
    }),
    /***
     * TODO (storytelling) abstract this state into a keyed object for performance
     */
    updateBlock: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'UPDATE_BLOCK') {
          return context.groups;
        }

        return context.groups.map((group) => ({
          ...group,
          items: group.items.map((item) => {
            // Update the block if it matches the event block ID
            if (item.id === event.block.id) {
              return event.block;
            }
            return item;
          }),
        }));
      },
    }),
    updateBlockType: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'UPDATE_BLOCK_TYPE') {
          return context.groups;
        }
        return mapToUpdatedBlockType(context, event);
      },
    }),
    addBlockToGroup: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'ADD_BLOCK_TO_GROUP') {
          return context.groups;
        }
        return context.groups.map((group) => {
          if (group.id === event.groupId) {
            return {
              ...group,
              items: [...group.items, event.block],
            };
          }
          return group;
        });
      },
    }),
    /**
     * group level actions
     */
    createGroup: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'CREATE_GROUP') {
          return context.groups;
        }
        return createNewGroup(context, event);
      },
      focusedGroupId: ({ context, event }) => {
        if (event.type === 'CREATE_GROUP') {
          return event.groupId;
        }
        return context.focusedGroupId;
      },
    }),
    removeGroup: assign({
      caretPosition: 0,
      contentLength: 0,
      groups: ({ context, event }) => {
        if (event.type !== 'REMOVE_GROUP' && event.type !== 'PRESS_BACKSPACE') {
          return context.groups;
        }
        return context.groups.filter((group) => group.id !== event.groupId);
      },
      focusedGroupId: ({ context, event }) => {
        if (event.type !== 'REMOVE_GROUP' && event.type !== 'PRESS_BACKSPACE') {
          return context.focusedGroupId;
        }
        return findNextFocusedGroup(context, event);
      },
    }),
    setGroups: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'SET_GROUPS') {
          return context.groups;
        }
        return event.groups;
      },
    }),
    updateGroupSize: assign({
      groups: ({ context, event }) => {
        if (event.type !== 'UPDATE_GROUP_SIZE') {
          return context.groups;
        }
        return context.groups.map((group) => {
          // Update the size if the group matches the event groupId
          if (group.id === event.groupId) {
            return {
              ...group,
              size: event.size,
            };
          }
          return group;
        });
      },
    }),
    /**
     * focus and blur actions
     */
    focusGroup: assign({
      focusedGroupId: ({ context, event }) => {
        if (event.type === 'FOCUS_GROUP') {
          return event.groupId;
        }
        return context.focusedGroupId;
      },
    }),
    focusNextGroup: assign({
      focusedGroupId: ({ context }) => {
        if (context.focusedGroupId == null) {
          return null;
        }
        return cycleList(groupsToIdList(context.groups), {
          id: context.focusedGroupId,
        }).next().id;
      },
    }),
    focusPreviousGroup: assign({
      focusedGroupId: ({ context }) => {
        if (context.focusedGroupId == null) {
          return null;
        }
        return cycleList(groupsToIdList(context.groups), {
          id: context.focusedGroupId,
        }).prev().id;
      },
    }),
    blurGroup: assign({
      focusedGroupId: ({ context, event }) => {
        if (event.type === 'BLUR_GROUP') {
          return null;
        }
        return context.focusedGroupId;
      },
      lastFocusedGroupId: ({ context, event }) => {
        if (event.type === 'BLUR_GROUP') {
          return context.focusedGroupId;
        }
        return context.lastFocusedGroupId;
      },
      caretPosition: 0,
      contentLength: 0,
    }),

    clearLastFocusedGroup: assign({
      lastFocusedGroupId: null,
    }),

    updateEditorState: assign({
      caretPosition: ({ context, event }) => {
        if (
          event.type !== 'UPDATE_EDITOR_STATE' ||
          event.groupId !== context.focusedGroupId
        ) {
          return context.caretPosition;
        }
        return event.caretPosition;
      },
      contentLength: ({ context, event }) => {
        if (
          event.type !== 'UPDATE_EDITOR_STATE' ||
          event.groupId !== context.focusedGroupId
        ) {
          return context.contentLength;
        }
        return event.contentLength;
      },
    }),
  },
  guards: {
    hasMomentId: ({ context }) => {
      return isNumberType(context.momentId);
    },
    // TODO (storytelling) can we guard this by the group type?
    isCaretAtEnd: ({ context }) => {
      return context.caretPosition === context.contentLength;
    },
    // TODO (storytelling) can we guard this by the group type?
    isCaretAtStart: ({ context }) => {
      return context.caretPosition === 0;
    },
    // TODO (storytelling) can we guard this by the group type?
    isEditorEmpty: ({ context }) => {
      return context.contentLength === 0;
    },
    // TODO (storytelling) can we guard this by the group type?
    isEditorNotEmpty: ({ context }) => {
      return context.contentLength > 0;
    },
    isGroupsWithinLimit: ({ context }) => {
      return context.groups.length <= STORY_GROUP_LIMIT;
    },
  },
}).createMachine({
  id: 'editor',
  initial: 'awaitingMomentId',
  context: {
    momentId: null,
    groups: [],
    focusedGroupId: null,
    lastFocusedGroupId: null,
    caretPosition: 0,
    contentLength: 0,
    momentItems: [],
  },
  states: {
    awaitingMomentId: {
      tags: ['loading'],
      on: {
        SET_MOMENT_ID: {
          target: 'loading',
          actions: assign({
            momentId: ({ event }) => event.momentId,
          }),
        },
      },
    },
    loading: {
      tags: ['loading'],
      invoke: {
        id: 'fetchInitialStory',
        src: 'storyFetcher',
        input: ({ context }) => ({
          momentId: context.momentId || 0,
        }),
        onDone: {
          target: 'idle',
          actions: assign({
            groups: ({ event }) => event.output.groups,
            momentItems: ({ event }) => event.output.momentItems,
          }),
        },
      },
    },
    idle: {
      tags: ['ready'],
      after: {
        timeout: {
          actions: 'clearLastFocusedGroup',
        },
      },
      on: {
        ADD_BLOCK_TO_GROUP: {
          actions: 'addBlockToGroup',
        },
        REMOVE_BLOCK: {
          actions: 'removeBlock',
        },
        UPDATE_GROUP_SIZE: {
          actions: 'updateGroupSize',
        },
        UPDATE_BLOCK: {
          actions: 'updateBlock',
        },
        UPDATE_BLOCK_TYPE: {
          actions: 'updateBlockType',
        },
        CREATE_GROUP: {
          target: 'focused',
          actions: 'createGroup',
          guard: 'isGroupsWithinLimit',
        },
        REMOVE_GROUP: {
          actions: 'removeGroup',
        },
        FOCUS_GROUP: {
          target: 'focused',
          actions: 'focusGroup',
        },
        SCROLL_TO_GROUP: {
          target: 'scrolling',
        },
        SET_GROUPS: {
          actions: 'setGroups',
        },
      },
    },
    scrolling: {
      tags: ['ready', 'scrolling'],
      on: {
        SCROLL_COMPLETE: {
          target: 'focused',
        },
      },
    },
    focused: {
      tags: ['ready'],
      after: {
        timeout: {
          actions: 'clearLastFocusedGroup',
        },
      },
      on: {
        ADD_BLOCK_TO_GROUP: {
          actions: 'addBlockToGroup',
        },
        REMOVE_BLOCK: {
          actions: 'removeBlock',
        },
        UPDATE_GROUP_SIZE: {
          actions: 'updateGroupSize',
        },
        UPDATE_BLOCK: {
          actions: 'updateBlock',
        },
        UPDATE_BLOCK_TYPE: {
          actions: 'updateBlockType',
        },
        UPDATE_EDITOR_STATE: {
          actions: 'updateEditorState',
        },
        CREATE_GROUP: {
          actions: 'createGroup',
          guard: 'isGroupsWithinLimit',
        },
        REMOVE_GROUP: {
          actions: 'removeGroup',
        },
        SET_GROUPS: {
          actions: 'setGroups',
        },
        BLUR_GROUP: {
          target: 'idle',
          actions: 'blurGroup',
        },
        SCROLL_TO_GROUP: {
          target: 'scrolling',
        },
        PRESS_ENTER: {
          guard: 'isEditorNotEmpty',
          actions: raise(() => ({
            type: 'CREATE_GROUP',
            groupId: uuidv4(),
            items: [
              {
                id: uuidv4(),
                type: 'PARAGRAPH',
                content: STORY_PARAGRAPH_PLACEHOLDER,
                placeholder: STORY_COPY.TEXT_PLACEHOLDER,
              },
            ],
          })),
        },
        PRESS_ARROW_DOWN: {
          guard: 'isCaretAtEnd',
          actions: 'focusNextGroup',
        },
        PRESS_ARROW_UP: {
          guard: 'isCaretAtStart',
          actions: 'focusPreviousGroup',
        },
        PRESS_BACKSPACE: {
          guard: 'isEditorEmpty',
          actions: 'removeGroup',
        },
      },
    },
  },
});

export const EditorMachineContext = createActorContext(editorMachine);

function findNextFocusedGroup(
  context: EditorContext,
  event:
    | Extract<EditorEvent, { type: 'REMOVE_GROUP' }>
    | Extract<EditorEvent, { type: 'PRESS_BACKSPACE' }>
) {
  const filteredGroups = context.groups.filter(
    (group) => group.id !== event.groupId
  );

  if (filteredGroups.length === 0) {
    return null;
  }

  const cycle = cycleList(groupsToIdList(context.groups), {
    id: event.groupId,
  });

  const prev = cycle.prev();

  if (prev.id !== context.lastFocusedGroupId) {
    return prev.id;
  }

  const next = cycle.next();

  return next.id;
}

function mapToUpdatedBlockType(
  context: EditorContext,
  event: Extract<EditorEvent, { type: 'UPDATE_BLOCK_TYPE' }>
) {
  return context.groups.map((group) => {
    if (!isTextStoryGroup(group)) {
      return group;
    }

    return {
      ...group,
      items: group.items.map((item) => {
        if (item.id === event.blockId) {
          // TODO (storytelling) abstract this helper with tests
          return match<StoryTextBlockType, StoryTextBlock>(event.contentType)
            .with('PARAGRAPH', () => ({
              type: 'PARAGRAPH',
              id: item.id,
              content: generateParagraphJsonFromText(event.contentText),
              placeholder: item.placeholder,
            }))
            .with('BLOCKQUOTE', () => ({
              type: 'BLOCKQUOTE',
              id: item.id,
              content: generateBlockquoteJsonFromText({
                text: event.contentText,
                cite: '',
              }),
              placeholder: item.placeholder,
            }))
            .with('H1', () => ({
              type: 'H1',
              id: item.id,
              content: generateH1JsonFromText(event.contentText),
              placeholder: item.placeholder,
            }))
            .with('H2', () => ({
              type: 'H2',
              id: item.id,
              content: generateH2JsonFromText(event.contentText),
              placeholder: item.placeholder,
            }))
            .exhaustive();
        }
        return item;
      }),
    };
  });
}

function createNewGroup(
  context: EditorContext,
  event: Extract<EditorEvent, { type: 'CREATE_GROUP' }>
) {
  const newGroup: StoryGroup = {
    id: event.groupId,
    size: 'NONE',
    items: event.items,
  };

  const groupToInsertAfter =
    context.focusedGroupId || context.lastFocusedGroupId;

  if (groupToInsertAfter) {
    // If no editor is focused, add the new editor at the end
    // Find the index of the currently focused editor
    const focusedIndex = context.groups.findIndex(
      (group) => group.id === groupToInsertAfter
    );
    // Insert the new editor after the focused editor
    return [
      ...context.groups.slice(0, focusedIndex + 1),
      newGroup,
      ...context.groups.slice(focusedIndex + 1),
    ];
  } else {
    return [...context.groups, newGroup];
  }
}

function groupsToIdList(groups: StoryGroup[]) {
  return groups.map((group) => ({ id: group.id }));
}

function cycleList<Item extends { id: string }>(
  items: Item[],
  currentItem: Item
): { next: () => Item; prev: () => Item } {
  let currentIndex = items.findIndex((item) => item.id === currentItem.id);

  return {
    next: () => {
      // Check if already at the end
      if (currentIndex === items.length - 1) {
        return currentItem;
      }
      currentIndex = (currentIndex + 1) % items.length;
      return items[currentIndex] || currentItem;
    },
    prev: () => {
      // Check if already at the start
      if (currentIndex === 0) {
        return currentItem;
      }
      currentIndex = (currentIndex - 1 + items.length) % items.length;
      return items[currentIndex] || currentItem;
    },
  };
}
